import { zodResolver } from '@hookform/resolvers/zod';
import { Checkbox, FormControlLabel, Grid } from '@mui/material';
import React, { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { z } from 'zod';
import {
  DealerField,
  EmailField,
  EntityProfilePage,
  ManufacturerField,
  PhoneNumberField,
  SelectField,
  StatusIndicator,
  TextInput,
} from '../../components/form';
import { RootState } from '../../store';
import { useAppSelector } from '../../store/hooks.ts';
import { User, UserRole } from '../../types';
import {
  emailSchema,
  formatPhoneNumber,
  getRoleLabel,
  getStatusLabel,
  logError,
  phoneNumberSchema,
  preventPaste,
  showToast,
  unformatPhoneNumber,
  useImageUpload,
} from '../../utils';
import { useGetDealersByManufacturerIdQuery } from '../dealer';
import { useManufacturerData } from '../manufacturer/use-manufacturer-data';
import { selectManageDealers, selectManageManufacturers, selectManageUsers } from './user-slice.ts';
import {
  useAddUserMutation,
  useGetUserByIdQuery,
  useUpdateUserMutation,
} from './users-api-slice.ts';

// Define Zod schema for form validation
const userFormSchema = z
  .object({
    firstName: z.string().min(1, 'First name is required').max(250, 'First name is too long'),
    lastName: z.string().min(1, 'Last name is required').max(250, 'Last name is too long'),
    email: emailSchema,
    emailConfirmation: z.string().min(1, 'Please confirm your email'),
    phone: phoneNumberSchema,
    role: z.nativeEnum(UserRole).refine(val => val !== undefined, {
      message: 'Role is required',
    }),
    active: z.boolean(),
    allowSignOffOnTasks: z.boolean().optional(),
    manufacturerId: z.coerce
      .number()
      .optional()
      .refine(
        val => {
          // This ensures that manufacturerId is not empty/undefined/zero when provided
          return !val || val > 0;
        },
        { message: 'Please select a valid manufacturer' }
      ),
    dealerId: z.coerce.number().optional(),
  })
  .refine(data => data.email === data.emailConfirmation, {
    message: 'Email addresses do not match',
    path: ['emailConfirmation'],
  })
  .refine(
    data => {
      return (
        (data.role !== UserRole.SUPERVISOR && data.role !== UserRole.CSR) ||
        (data.manufacturerId !== undefined && data.manufacturerId > 0)
      );
    },
    {
      message: 'Manufacturer is required for Supervisor role',
      path: ['manufacturerId'],
    }
  )
  .refine(
    data => {
      return (
        data.role !== UserRole.DEALER ||
        (data.manufacturerId !== undefined &&
          data.manufacturerId > 0 &&
          data.dealerId !== undefined)
      );
    },
    {
      message: 'Manufacturer and Dealer are required for CSR and Dealer roles',
      path: ['dealerId'],
    }
  );

// Extract type from Zod schema
type UserFormValues = z.infer<typeof userFormSchema>;

const UserProfilePage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEditMode = !!id;

  // Get current user from Redux store
  const currentUser = useSelector((state: RootState) => state.user.currentUser);
  const currentUserRole = currentUser?.role;
  const currentUserManufacturerId = currentUser?.manufacturerId;
  const isAdmin = currentUserRole === UserRole.ADMINISTRATOR;
  const isSupervisor = currentUserRole === UserRole.SUPERVISOR;
  const canManageUsers = useAppSelector(selectManageUsers);
  const canManageManufacturers = useAppSelector(selectManageManufacturers);
  const canManageDealers = useAppSelector(selectManageDealers);

  // Use the shared image upload hook
  const {
    imageFile: avatarFile,
    imagePreview: avatarPreview,
    imageChanged: avatarChanged,
    handleImageChange: handleAvatarChange,
    resetImageState: resetAvatarState,
  } = useImageUpload();

  // Get user data
  const {
    data: user,
    isLoading,
    error,
    refetch,
  } = useGetUserByIdQuery(id ?? '', {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });

  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const [addUser, { isLoading: isAdding }] = useAddUserMutation();

  // Get available roles based on current user's role
  const getAvailableRoles = () => {
    if (currentUserRole === UserRole.ADMINISTRATOR) {
      return Object.values(UserRole);
    } else if (currentUserRole === UserRole.SUPERVISOR) {
      return [UserRole.CSR, UserRole.DEALER];
    } else {
      // For other roles, return the existing role (for read-only display)
      return Object.values(UserRole);
    }
  };

  // Set up React Hook Form with Zod resolver
  const form = useForm<UserFormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      emailConfirmation: '',
      phone: '',
      role: (() => {
        // Set initial role based on user permissions for new users
        if (!isEditMode) {
          if (canManageManufacturers) {
            return UserRole.SUPERVISOR;
          } else if (canManageDealers) {
            return UserRole.DEALER;
          }
        }
        return UserRole.CSR;
      })(),
      active: true,
      allowSignOffOnTasks: false,
      manufacturerId: undefined,
      dealerId: undefined,
    },
    resolver: zodResolver(userFormSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const {
    control,
    reset,
    watch,
    setValue,
    trigger,
    formState: { isDirty, isValid, touchedFields },
  } = form;

  // Watch both email fields to handle synchronization
  const watchedEmail = useWatch({
    control,
    name: 'email',
  });

  const watchedEmailConfirmation = useWatch({
    control,
    name: 'emailConfirmation',
  });

  // Watch the role to show/hide manufacturer dropdown
  const watchedRole = useWatch({
    control,
    name: 'role',
  });

  // Watch the manufacturerId to filter dealers
  const watchedManufacturerId = useWatch({
    control,
    name: 'manufacturerId',
  });

  const { manufacturers, isLoading: isLoadingManufacturers } = useManufacturerData({
    isEditMode,
    profileManufacturerId: user?.manufacturerId,
    currentUserManufacturerId,
  });

  const { data: manufacturerDealers = [], isLoading: isLoadingDealers } =
    useGetDealersByManufacturerIdQuery(watchedManufacturerId ?? 0, {
      skip: !watchedManufacturerId,
      refetchOnMountOrArgChange: true,
    });

  const isInProcess = Boolean(
    isUpdating || isAdding || isLoadingManufacturers || (watchedManufacturerId && isLoadingDealers)
  );

  // Filter dealers: show active dealers plus the user's currently assigned dealer (even if inactive)
  const filteredDealers = React.useMemo(() => {
    if (!watchedManufacturerId || !manufacturerDealers.length) return [];

    const activeDealers = manufacturerDealers.filter(
      dealer => dealer.manufacturerId === watchedManufacturerId && dealer.status === true
    );

    if (user?.dealerId) {
      const currentDealerIncluded = activeDealers.some(d => d.id === user.dealerId);
      if (!currentDealerIncluded) {
        const currentDealer = manufacturerDealers.find(d => d.id === user.dealerId);
        if (currentDealer) {
          return [currentDealer, ...activeDealers];
        }
      }
    }

    return activeDealers;
  }, [manufacturerDealers, watchedManufacturerId, user?.dealerId]);

  // Use a combined dirty state that includes both form changes and avatar changes
  const hasChanges = isDirty || avatarChanged;

  // Handle form submission with React Hook Form
  const onSubmit = async (data: UserFormValues) => {
    try {
      // Process phone number to store only digits
      const processedData = {
        ...data,
        phone: unformatPhoneNumber(data.phone),
      };

      if (id) {
        await updateUser({
          id,
          ...processedData,
          avatarFile: avatarFile || undefined,
        }).unwrap();
        showToast('User successfully updated', 'success');
      } else {
        const newUser: Omit<User, 'id'> = {
          firstName: processedData.firstName,
          lastName: processedData.lastName,
          email: processedData.email,
          phone: processedData.phone,
          role: processedData.role,
          active: true, // Always set to active for new users
          allowSignOffOnTasks: processedData.allowSignOffOnTasks ?? false,
          manufacturerId: processedData.manufacturerId,
          dealerId: processedData.dealerId,
          dealer: undefined,
        };

        await addUser({
          userData: newUser,
          avatarFile: avatarFile || undefined,
        }).unwrap();

        showToast('User successfully created', 'success');
      }

      resetAvatarState();
      return Promise.resolve();
    } catch (error) {
      logError('Error updating user:', error);
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    }
  };

  useEffect(() => {
    if (id) {
      refetch();
    }
  }, [id, refetch]);

  useEffect(() => {
    // Clear the selected dealer when manufacturer changes
    if (watchedManufacturerId) {
      // Only reset if not in edit mode or if in edit mode but different manufacturer
      if (!isEditMode || (user && user.manufacturerId !== watchedManufacturerId)) {
        setValue('dealerId', undefined);
      }
    }
  }, [watchedManufacturerId, setValue, isEditMode, user]);

  // Sync email fields when primary email changes to match confirmation
  useEffect(() => {
    if (
      watchedEmail !== undefined &&
      watchedEmailConfirmation !== undefined &&
      (touchedFields.email || touchedFields.emailConfirmation)
    ) {
      // They match now, so trigger validation to clear any error messages
      trigger('emailConfirmation').catch(error => {
        logError('Error triggering validation:', error);
      });
    }
  }, [
    watchedEmail,
    watchedEmailConfirmation,
    setValue,
    touchedFields.email,
    touchedFields.emailConfirmation,
    trigger,
  ]);

  // For new user creation
  useEffect(() => {
    if (!isEditMode && !isAdmin && currentUserManufacturerId) {
      // Set the manufacturer ID to the current user's manufacturer for non-admin users
      setValue('manufacturerId', currentUserManufacturerId);
    }
  }, [isEditMode, isAdmin, currentUserManufacturerId, setValue]);

  useEffect(() => {
    if (user && manufacturers.length > 0) {
      const effectiveManufacturerId =
        !isAdmin && currentUserManufacturerId ? currentUserManufacturerId : user?.manufacturerId;

      // Reset form with user data
      reset({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        emailConfirmation: user.email,
        phone: user.phone,
        role: user.role,
        active: user.active,
        allowSignOffOnTasks: user.allowSignOffOnTasks ?? false,
        manufacturerId: effectiveManufacturerId ?? undefined,
        dealerId: user.dealerId ?? undefined,
      });
    }
  }, [user, reset, manufacturers.length, isAdmin, currentUserManufacturerId]);

  useEffect(() => {
    // If the role changed (not during initial form setup)
    if (isDirty && touchedFields.role) {
      // Clear both manufacturer and dealer values
      setValue('manufacturerId', undefined);
      setValue('dealerId', undefined);

      // If the user is a SUPERVISOR, CSR, or DEALER and the current user has a manufacturerId,
      // automatically set it for non-admin users
      if (
        !isAdmin &&
        currentUserManufacturerId &&
        (watchedRole === UserRole.SUPERVISOR ||
          watchedRole === UserRole.CSR ||
          watchedRole === UserRole.DEALER)
      ) {
        setValue('manufacturerId', currentUserManufacturerId);
      }
    }
  }, [
    watchedRole,
    setValue,
    isDirty,
    touchedFields.role,
    isAdmin,
    currentUserManufacturerId,
    form,
  ]);

  return (
    <EntityProfilePage
      title="User Profile"
      entity={user}
      isLoading={isLoading}
      error={error}
      isSaving={isInProcess}
      form={form}
      hasChanges={hasChanges}
      isValid={isValid}
      imageName="avatar"
      imageUrl={avatarPreview ?? user?.avatar}
      imageAlt={`${watch('firstName')} ${watch('lastName')}`}
      uploadButtonText="Upload Photo"
      roundedImage={true}
      onSubmit={onSubmit}
      onImageChange={handleAvatarChange}
      isReadOnly={!canManageUsers}
    >
      <Grid container spacing={2}>
        {/* Role selection */}
        <Grid item xs={12}>
          <SelectField
            name="role"
            control={control}
            label="Role"
            options={getAvailableRoles().map(role => ({
              value: role,
              label: getRoleLabel(role),
            }))}
            isReadOnly={isEditMode}
            required={true}
          />
        </Grid>

        {canManageManufacturers &&
          (watchedRole === UserRole.SUPERVISOR ||
            watchedRole === UserRole.CSR ||
            watchedRole === UserRole.DEALER) && (
            <Grid item xs={12}>
              <ManufacturerField
                control={control}
                name="manufacturerId"
                manufacturers={manufacturers}
                isReadOnly={!canManageManufacturers || isEditMode}
                required={true}
                isLoading={isLoadingManufacturers}
                getManufacturerName={() => {
                  return (
                    manufacturers.find(m => m.id === user?.manufacturerId)?.licenseeName ?? 'None'
                  );
                }}
              />
            </Grid>
          )}

        {/* Dealer field - only DEALER roles */}
        {watchedRole === UserRole.DEALER && (
          <DealerField
            name="dealerId"
            control={control}
            dealers={filteredDealers}
            isReadOnly={!canManageUsers}
            required={true}
            isLoading={isLoadingDealers}
            requiresManufacturer={true}
            hasManufacturer={!!watchedManufacturerId}
            getDealerName={() => {
              return (
                filteredDealers.find(d => d.id === user?.dealerId)?.name ??
                manufacturerDealers.find(d => d.id === user?.dealerId)?.name ??
                'None'
              );
            }}
          />
        )}

        {/* First name */}
        <Grid item xs={12} sm={6}>
          <TextInput
            name="firstName"
            control={control}
            label="First Name"
            isReadOnly={!canManageUsers}
            required={true}
            maxLength={250}
          />
        </Grid>

        {/* Last name */}
        <Grid item xs={12} sm={6}>
          <TextInput
            name="lastName"
            control={control}
            label="Last Name"
            isReadOnly={!canManageUsers}
            required={true}
            maxLength={250}
          />
        </Grid>

        {/* Email Fields */}
        <EmailField
          emailName="email"
          confirmationName="emailConfirmation"
          control={control}
          handlePaste={preventPaste}
          sm={6}
          allowPaste={false}
          allowAutofill={false}
          isReadOnly={!canManageUsers || isEditMode}
        />

        {/* Phone */}
        <Grid item xs={12}>
          <PhoneNumberField
            name="phone"
            control={control}
            formatPhoneNumber={formatPhoneNumber}
            label="Phone Number"
            isReadOnly={!canManageUsers}
            required
          />
        </Grid>

        {/* Status toggle - only show for edit mode */}
        {isEditMode && (
          <Grid item xs={12}>
            <StatusIndicator
              name="active"
              control={control}
              activeText={getStatusLabel(true)}
              inactiveText={getStatusLabel(false)}
              isReadOnly={!canManageUsers}
            />
          </Grid>
        )}

        {/* Allowed to Sign Off on Tasks - Supervisor only (CSRs are read-only) */}
        {watchedRole === UserRole.SUPERVISOR && (
          <Grid item xs={12}>
            <Controller
              name="allowSignOffOnTasks"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!field.value}
                      onChange={e => field.onChange(e.target.checked)}
                      disabled={!(isAdmin || isSupervisor)}
                      color="primary"
                    />
                  }
                  label="Allowed to Sign Off on Tasks"
                />
              )}
            />
          </Grid>
        )}
      </Grid>
    </EntityProfilePage>
  );
};

export default UserProfilePage;
